import { createFileRoute, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Megaphone,
  Plus,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Users,
  ShoppingBag,
  Wallet,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { CampaignItem } from '@/lib/api';
import { BottomSheet } from '@/components/ui/dialog';
import {
  Skeleton,
  SkeletonCard,
  SkeletonLine,
  SkeletonPage,
  SkeletonStats,
} from '@/components/ui/skeleton';
import { getWebApp, haptic } from '@/lib/telegram';

export const Route = createFileRoute('/campaigns')({
  component: Campaigns,
  // botId опционален — навигация на /campaigns без search должна оставаться
  // валидной (дашборд, профиль, стата). Ключ присутствует только при заходе
  // с карточки бота.
  validateSearch: (search: Record<string, unknown>): { botId?: string } => {
    const botId = search.botId as string | undefined;
    return botId ? { botId } : {};
  },
});

// Универсальная навигация к деталям кампании. В iOS Telegram WKWebView
// TanStack Router navigate() иногда не срабатывает (известный нюанс с history
// API в Mini App) — fallback на window.location, если URL не сменился за 200ms.
// Тот же паттерн, что в bots.tsx (goToBot).
type NavFn = ReturnType<typeof useNavigate>;
function goToCampaign(nav: NavFn, campaignId: string) {
  const target = `/campaigns/${campaignId}`;
  const before = window.location.pathname;
  try {
    void nav({ to: '/campaigns/$campaignId', params: { campaignId } });
  } catch {
    // ignore — упадём в fallback
  }
  window.setTimeout(() => {
    if (window.location.pathname === before) {
      window.location.assign(target);
    }
  }, 200);
}

/** USD-cents → целые ₽ по живому курсу (как в dashboardStats: cents × rate / 100). */
export function usdMinorToRub(minor: number, fxRate: number): number {
  return Math.round((minor * fxRate) / 100);
}

export function fmtRub(rub: number): string {
  return `${rub.toLocaleString('ru')} ₽`;
}

/** Траты в исходных валютах: «1 500 ₽ + $20». */
export function fmtSpend(spend: Partial<Record<'RUB' | 'USD', number>>): string {
  const parts: string[] = [];
  if (spend.RUB) parts.push(`${Math.round(spend.RUB / 100).toLocaleString('ru')} ₽`);
  if (spend.USD) parts.push(`$${(spend.USD / 100).toLocaleString('ru')}`);
  return parts.length ? parts.join(' + ') : '—';
}

export function RoiPill({ roi }: { roi: number | null }) {
  if (roi === null) {
    return (
      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/[0.06] text-dim">
        ROI —
      </span>
    );
  }
  const positive = roi >= 0;
  return (
    <span
      className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded ${
        positive ? 'bg-money/15 text-money-light' : 'bg-danger/15 text-danger'
      }`}
    >
      ROI {positive ? '+' : ''}
      {Math.round(roi)}%
    </span>
  );
}

function Campaigns() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { botId } = Route.useSearch();
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // campaigns.$campaignId.tsx — ДОЧЕРНИЙ роут для campaigns.tsx (flat-routing).
  // На /campaigns/<id> рендерится этот компонент + Outlet с детьми. Без Outlet
  // ребёнок не отрисуется → URL меняется, контент нет (как в bots.tsx).
  const onChildRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith('/campaigns/'),
  });

  const { data } = useQuery({
    queryKey: ['campaigns', showArchived],
    queryFn: () => api.campaigns(showArchived),
    staleTime: 30_000,
    enabled: !onChildRoute,
  });

  useEffect(() => {
    if (onChildRoute) return;
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/dashboard' });
    tg.BackButton.onClick(onBack);
    tg.MainButton?.hide();
    return () => tg.BackButton.offClick(onBack);
  }, [navigate, onChildRoute]);

  // Условный return ПОСЛЕ всех хуков (Rules of Hooks).
  if (onChildRoute) return <Outlet />;
  if (!data) return <CampaignsSkeleton />;

  const fx = data.fx_rate_to_display > 0 ? data.fx_rate_to_display : 100;
  // Фильтр по боту (заход с карточки бота — «реклама этого бота»).
  const items = botId ? data.items.filter((c) => c.bot_id === botId) : data.items;
  const botName = botId ? items.find((c) => c.bot_username)?.bot_username ?? null : null;
  const active = items.filter((c) => c.status === 'active');
  const shown = showArchived ? items : active;

  const totalSpendUsd = items.reduce((s, c) => s + c.spend_usd_minor, 0);
  const totalMarginUsd = items.reduce((s, c) => s + c.margin_minor, 0);
  const totalRoi =
    totalSpendUsd > 0 ? ((totalMarginUsd - totalSpendUsd) / totalSpendUsd) * 100 : null;

  return (
    <div className="p-4 space-y-3 pb-28">
      <div className="flex items-center justify-between px-1 pt-2">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-bold tracking-tight">Реклама</h2>
          {botName && <div className="text-[11px] text-dim truncate">@{botName}</div>}
        </div>
        <button
          type="button"
          onClick={() => {
            haptic('selection');
            setCreateOpen(true);
          }}
          className="bg-primary text-primary-foreground rounded-[13px] px-3.5 py-2 text-sm font-bold flex items-center gap-1.5 active:scale-[0.97] transition-transform"
        >
          <Plus className="w-4 h-4" />
          Кампания
        </button>
      </div>

      <p className="text-[12.5px] text-muted-foreground leading-relaxed px-1">
        Создайте ссылку под каждый закуп рекламы: будет видно, сколько людей пришло,
        что купили и окупился ли закуп. Приходы и покупки считаются по тому боту,
        на который создана кампания.
      </p>

      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-2.5">
          <SummaryTile
            label="Потрачено"
            value={fmtRub(usdMinorToRub(totalSpendUsd, fx))}
            tone="amber"
          />
          <SummaryTile
            label="Маржа"
            value={fmtRub(usdMinorToRub(totalMarginUsd, fx))}
            tone="money"
          />
          <SummaryTile
            label="ROI"
            value={totalRoi === null ? '—' : `${totalRoi >= 0 ? '+' : ''}${Math.round(totalRoi)}%`}
            tone={totalRoi !== null && totalRoi < 0 ? 'danger' : 'primary'}
          />
        </div>
      )}

      {shown.length === 0 ? (
        <div className="surface-card rounded-[22px] p-8 text-center space-y-2">
          <Megaphone className="w-8 h-8 mx-auto text-dim" />
          <p className="text-sm text-muted-foreground">
            Кампаний ещё нет. Создайте первую и получите ссылку для рекламы.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {shown.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              fx={fx}
              onClick={() => goToCampaign(navigate, c.id)}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowArchived((v) => !v)}
        className="w-full text-center text-xs text-dim py-1"
      >
        {showArchived ? 'Скрыть архивные' : 'Показать архивные'}
      </button>

      {!botId && <RawUtmBlock fx={fx} />}

      <CreateCampaignSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultBotId={botId}
        onCreated={(c) => {
          void qc.invalidateQueries({ queryKey: ['campaigns'] });
          setCreateOpen(false);
          goToCampaign(navigate, c.id);
        }}
      />
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'amber' | 'money' | 'primary' | 'danger';
}) {
  const toneCls =
    tone === 'money'
      ? 'text-money-light'
      : tone === 'amber'
        ? 'text-amber'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-primary-light';
  return (
    <div className="surface-card rounded-[18px] p-3">
      <div className="text-[10.5px] text-dim">{label}</div>
      <div className={`font-display text-[17px] font-bold nums mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function CampaignCard({
  campaign: c,
  fx,
  onClick,
}: {
  campaign: CampaignItem;
  fx: number;
  onClick: () => void;
}) {
  const archived = c.status === 'archived';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left surface-card rounded-[20px] p-4 space-y-2.5 active:scale-[0.99] transition-transform ${archived ? 'opacity-55' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold text-sm truncate">
            {c.name}
            {archived && <span className="text-dim font-normal"> · архив</span>}
          </div>
          <div className="text-[10.5px] text-dim truncate mt-0.5">
            {c.bot_username ? `@${c.bot_username}` : 'бот удалён'}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RoiPill roi={c.roi_percent} />
          <ChevronRight className="w-4 h-4 text-dim" />
        </div>
      </div>
      <div className="flex items-center gap-3 text-[11.5px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <Users className="w-3.5 h-3.5" />
          {c.starts}
        </span>
        <span className="text-dim">→</span>
        <span className="flex items-center gap-1">
          <ShoppingBag className="w-3.5 h-3.5" />
          {c.buyers}
          {c.starts > 0 && <span className="text-dim">({Math.round(c.conversion_percent)}%)</span>}
        </span>
        <span className="ml-auto flex items-center gap-1 font-semibold text-money-light nums">
          <Wallet className="w-3.5 h-3.5" />
          {fmtRub(usdMinorToRub(c.margin_minor, fx))}
        </span>
      </div>
      {c.spend_usd_minor > 0 && (
        <div className="text-[11px] text-dim">Потрачено: {fmtSpend(c.spend_by_currency)}</div>
      )}
    </button>
  );
}

function CreateCampaignSheet({
  open,
  onClose,
  onCreated,
  defaultBotId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: CampaignItem) => void;
  defaultBotId?: string | null;
}) {
  const [name, setName] = useState('');
  const [botId, setBotId] = useState<string | null>(defaultBotId ?? null);

  const { data: bots } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    enabled: open,
  });
  const activeBots = (bots ?? []).filter((b) => b.status === 'active');

  useEffect(() => {
    if (!open) return;
    // Заход с карточки бота → бот предвыбран. Иначе авто-выбор если бот один.
    if (defaultBotId && !botId) setBotId(defaultBotId);
    else if (activeBots.length === 1 && !botId) setBotId(activeBots[0].id);
  }, [open, activeBots, botId, defaultBotId]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!botId) throw new Error('no bot');
      return api.createCampaign(name, botId);
    },
    onSuccess: (c) => {
      haptic('success');
      toast.success('Кампания создана');
      setName('');
      onCreated(c);
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось создать кампанию');
    },
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="Новая кампания">
      <p className="text-sm text-muted-foreground leading-relaxed">
        Назовите кампанию по месту закупа: «Канал Х июль», «Посев №3». Получите
        уникальную ссылку на бота: все, кто придёт по ней, будут видны в статистике.
      </p>

      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Название
        </label>
        <input
          type="text"
          maxLength={128}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Канал Х июль"
          className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none focus:border-white/30"
        />
      </div>

      {activeBots.length > 1 && (
        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Бот
          </label>
          <div className="space-y-2">
            {activeBots.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBotId(b.id)}
                className={`w-full text-left p-3 rounded-[15px] border transition-colors duration-100 flex items-center justify-between ${
                  botId === b.id ? 'border-white/30 bg-white/[0.07]' : 'surface-inset'
                }`}
              >
                <span className="font-bold text-sm">@{b.username}</span>
                {botId === b.id && <Check className="w-4 h-4 text-primary-light" />}
              </button>
            ))}
          </div>
        </div>
      )}
      {activeBots.length === 0 && (
        <p className="text-xs text-amber px-1">
          Сначала создайте бота: кампания это ссылка на него.
        </p>
      )}

      <button
        type="button"
        disabled={!name.trim() || !botId || isPending}
        onClick={() => mutate()}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Создать кампанию
      </button>
    </BottomSheet>
  );
}

/** «Прочий UTM-трафик» — свободные utm-ссылки, не совпавшие с кампаниями. */
function RawUtmBlock({ fx }: { fx: number }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['utm-traffic'],
    queryFn: () => api.utmTraffic(),
    enabled: open,
    staleTime: 60_000,
  });

  return (
    <div className="surface-card rounded-[20px] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-sm font-bold"
      >
        <span>Прочий UTM-трафик</span>
        <ChevronDown className={`w-4 h-4 text-dim transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-1">
          <p className="text-[11px] text-dim pb-1.5">
            Клиенты, пришедшие по ссылкам вида <code className="font-mono">?start=utm_...</code>,
            не совпавшим с кампаниями.
          </p>
          {!data ? (
            <Skeleton className="h-10 w-full" />
          ) : data.items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">Пока пусто.</p>
          ) : (
            data.items.map((r, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2 py-2 border-t border-white/[0.05] text-[12px]"
              >
                <div className="min-w-0">
                  <div className="font-mono text-[11.5px] truncate">
                    {[r.source, r.campaign, r.medium].filter(Boolean).join(' / ')}
                  </div>
                  <div className="text-[10.5px] text-dim mt-0.5 flex items-center gap-2.5 nums">
                    <span className="inline-flex items-center gap-1" title="Перешли в бота">
                      <Users className="w-3 h-3" aria-hidden />
                      {r.starts}
                    </span>
                    <span className="inline-flex items-center gap-1" title="Купили">
                      <ShoppingBag className="w-3 h-3" aria-hidden />
                      {r.buyers}
                    </span>
                  </div>
                </div>
                <div className="font-semibold text-money-light nums shrink-0">
                  {fmtRub(usdMinorToRub(r.margin_minor, fx))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Копируемая строка ссылки — используется и на детальной странице. */
export function CopyLinkRow({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const onOk = () => {
      setCopied(true);
      haptic('success');
      toast.success('Скопировано');
      setTimeout(() => setCopied(false), 1500);
    };
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        onOk();
        return;
      }
      throw new Error('no clipboard');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          onOk();
          return;
        }
      } catch {
        /* fall through */
      }
      getWebApp()?.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}`);
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="w-full surface-inset rounded-[14px] px-3.5 py-3 flex items-center gap-2 active:scale-[0.99] transition-transform text-left"
    >
      <code className="font-mono text-[11.5px] text-[#9fb8d6] truncate flex-1">{link}</code>
      {copied ? (
        <Check className="w-4 h-4 text-money-light shrink-0" />
      ) : (
        <Copy className="w-4 h-4 text-primary-light shrink-0" />
      )}
    </button>
  );
}

/**
 * Заголовок и пояснение под ним статичные. Плейсхолдерами кроем три сводные
 * плитки и карточки кампаний: имя с ботом, ROI-пилюля, строка «пришло → купило».
 */
function CampaignsSkeleton() {
  return (
    <SkeletonPage className="space-y-3 pb-28">
      <div className="flex items-center justify-between px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight">Реклама</h2>
        <Skeleton className="h-9 w-32 rounded-[13px]" />
      </div>
      <SkeletonStats count={3} cols={3} className="gap-2.5" />
      {[0, 1].map((i) => (
        <SkeletonCard key={i} className="rounded-[20px] min-h-0 p-4 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonLine className="w-2/5" />
              <SkeletonLine size="xs" className="w-1/4" />
            </div>
            <Skeleton className="h-[22px] w-16 rounded-full shrink-0" />
          </div>
          <div className="flex items-center gap-3">
            <SkeletonLine size="sm" className="w-14" />
            <SkeletonLine size="sm" className="w-14" />
            <SkeletonLine size="sm" className="w-20 ml-auto" />
          </div>
        </SkeletonCard>
      ))}
    </SkeletonPage>
  );
}
